import axios from 'axios';

/**
 * Custom error class for chat-specific errors.
 */
export class ChatError extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'ChatError';
    this.code = code;
  }
}

/**
 * Fetch list of available agents.
 * @returns {Promise<Array<{id, label, description, welcome_message}>>}
 */
export async function fetchAgents() {
  const response = await axios.get('/chat/agents');
  return response.data;
}

/**
 * Fetch internal agents (Admin-only, e.g. genia-intake). Same shape as
 * fetchAgents. #538 v2 — só chamar quando o usuário é Admin
 * (store.getters.isManagement); o backend também garante isso
 * (isAuthenticated + isManagement em /chat/internal-agents).
 * @returns {Promise<Array<{id, label, description, welcome_message}>>}
 */
export async function fetchInternalAgents() {
  const response = await axios.get('/chat/internal-agents');
  return response.data;
}

/**
 * Fetch the most recent conversation history for an agent.
 * @param {string} agentId
 * @returns {Promise<{conversationId, responseId, vectorStoreId, messages}>}
 */
export async function fetchHistory(agentId) {
  const response = await axios.get(`/chat/${agentId}/history`);
  return response.data;
}

/**
 * Send a message to a chat agent and stream the response.
 * Supports file attachments via FormData.
 *
 * @param {string} agentId - Agent slug
 * @param {string} input - User message
 * @param {string|null} previousResponseId - For conversation continuity
 * @param {AbortSignal} signal - For cancellation via AbortController
 * @param {File[]} files - New files to upload (optional)
 * @param {string[]} conversationFileIds - Existing image file IDs from previous turns (optional)
 * @param {string|null} vectorStoreId - Existing vector store ID from previous doc uploads (optional)
 * @yields {object} Parsed SSE event
 */
export async function* sendMessage(agentId, input, previousResponseId, signal, files, conversationFileIds, vectorStoreId, conversationId) {
  const token = localStorage.getItem('user-token');
  const baseURL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3000/api';

  const hasFiles = files && files.length > 0;
  // conversationFileIds can be array of { type, file_id } objects (preserves input type)
  const hasExistingFileIds = conversationFileIds && conversationFileIds.length > 0;

  let fetchOptions;

  if (hasFiles || hasExistingFileIds) {
    // Use FormData for multipart upload
    const formData = new FormData();
    formData.append('input', input || '');
    if (previousResponseId) {
      formData.append('previous_response_id', previousResponseId);
    }
    if (vectorStoreId) {
      formData.append('vector_store_id', vectorStoreId);
    }
    if (conversationId) {
      formData.append('conversation_id', conversationId);
    }
    if (hasExistingFileIds) {
      formData.append('file_ids', JSON.stringify(conversationFileIds));
    }
    for (const file of (files || [])) {
      formData.append('files', file);
    }

    fetchOptions = {
      method: 'POST',
      headers: {
        // Don't set Content-Type — browser sets multipart boundary automatically
        Authorization: `Bearer ${token}`,
      },
      body: formData,
      signal,
    };
  } else {
    // Use JSON for text-only messages (more efficient)
    const body = { input };
    if (previousResponseId) {
      body.previous_response_id = previousResponseId;
    }
    if (vectorStoreId) {
      body.vector_store_id = vectorStoreId;
    }
    if (conversationId) {
      body.conversation_id = conversationId;
    }

    fetchOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal,
    };
  }

  const response = await fetch(`${baseURL}/chat/${agentId}`, fetchOptions);

  // Handle HTTP errors before consuming stream
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch (e) {
      errorData = { error: `HTTP ${response.status}`, code: 'HTTP_ERROR' };
    }
    throw new ChatError(errorData.error || 'Erro no servidor', errorData.code || 'HTTP_ERROR');
  }

  // Consume SSE stream incrementally
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop();

      for (const line of lines) {
        if (!line.trim() || line.startsWith(':')) continue;
        if (line.startsWith('event:')) continue;

        if (line.startsWith('data:')) {
          const dataStr = line.slice(5).trim();
          if (!dataStr || dataStr === '[DONE]') continue;

          let parsed;
          try {
            parsed = JSON.parse(dataStr);
          } catch (e) {
            continue;
          }

          yield parsed;

          // Terminal events stop reading (but files_attached is NOT terminal)
          if (parsed.type === 'done' || parsed.type === 'error') {
            return;
          }
        }
      }
    }

    // Process remaining buffer
    if (buffer.trim() && buffer.startsWith('data:')) {
      const dataStr = buffer.slice(5).trim();
      if (dataStr && dataStr !== '[DONE]') {
        try {
          const parsed = JSON.parse(dataStr);
          yield parsed;
        } catch (e) {
          // Skip
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Cleanup vector store and files from OpenAI. Best-effort, fire-and-forget.
 * @param {string|null} vectorStoreId - Vector store to delete
 * @param {string[]} fileIds - File IDs to delete
 */
export async function cleanupConversation(vectorStoreId, fileIds) {
  if (!vectorStoreId && (!fileIds || fileIds.length === 0)) return;

  const token = localStorage.getItem('user-token');
  const baseURL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3000/api';

  try {
    await fetch(`${baseURL}/chat/cleanup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        vector_store_id: vectorStoreId || undefined,
        file_ids: fileIds || [],
      }),
    });
  } catch (e) {
    // Best-effort cleanup — don't block UI
    console.warn('Conversation cleanup failed:', e.message);
  }
}
